export const organizationalNavigationModule = {
  id: "ca-organizational-navigation",
  title: "Organizational Navigation Mastery",
  description: "Master corporate dynamics, build influence across hierarchies, and position yourself for maximum career impact.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Map formal and informal power structures within your organization
- Build influence and credibility across different organizational levels and departments
- Navigate corporate politics ethically while advancing your career objectives
- Position yourself visibly for promotion and advancement opportunities

## Prerequisites
- At least 6 months experience in your current organization
- Basic understanding of your company's structure and key stakeholders

## Overview
Organizational navigation skills separate high-potential employees from those who plateau despite strong technical abilities. Understanding corporate dynamics, building strategic relationships, and positioning yourself effectively can accelerate career advancement by 3-5 years. This module teaches the "unwritten rules" of corporate success.

---

## Key Concepts

### The Three-Layer Organizational Map
Every organization operates on three levels: formal structure (org chart), informal networks (who influences whom), and cultural dynamics (how decisions really get made).

#### Why It Matters
Professionals who understand all three organizational layers are 4x more likely to get promoted and 60% more effective at implementing initiatives. Surface-level understanding limits your ability to create meaningful change and advance your career.

#### Best Practices
- Map reporting relationships alongside actual decision-making influence
- Identify cultural carriers who shape organizational norms and values
- Understand resource allocation processes and budget cycles
- Recognize informal coalitions and alliance patterns

### Strategic Visibility and Positioning
Career advancement requires being seen as valuable by the right people at the right times. Strategic positioning ensures your contributions are visible to decision-makers.

#### Industry Usage
Executive search firms report that 70% of senior promotions go to candidates who had strategic visibility within their organizations. Being highly competent is necessary but not sufficient - you must also be known for your competence.

---

## Practical Examples

### Example 1: The Influence Map Exercise
Chart the real decision-making network in your organization.

**Mapping Template:**
- **Power Centers**: Who makes final decisions on budgets, strategy, personnel?
- **Influencers**: Who do power centers consult before making decisions?
- **Connectors**: Who bridges different departments and levels?
- **Information Brokers**: Who knows what's happening across the organization?
- **Culture Carriers**: Who embodies and shapes organizational values?

### Example 2: Strategic Project Selection
Choose assignments that build both capability and visibility.

**Selection Criteria:**
1. **Skill Development**: Does this stretch your capabilities meaningfully?
2. **Strategic Alignment**: Is this project important to organizational goals?
3. **Visibility Level**: Will success be noticed by advancement decision-makers?
4. **Network Building**: Does this create relationships with influential stakeholders?
5. **Success Probability**: Can you realistically deliver excellent results?

💡 **Pro Tip**: Use the "Sponsor, Champion, Advocate" model - identify executives who can sponsor your advancement, peers who champion your work, and direct reports who advocate for your leadership.

---

## Industry Adoption

### Why Companies Choose This Approach
Organizations benefit when employees understand corporate dynamics because it improves decision-making speed, reduces resistance to change, and identifies high-potential talent more effectively. Companies with transparent advancement processes show 30% better internal mobility.

### Popular Tools and Libraries
- **OrgMapper**: Organizational network analysis and relationship mapping
- **Culture Amp**: Employee engagement and organizational culture assessment
- **LinkedIn**: Professional relationship and influence tracking

### Comparison with Alternatives
| Approach | Advantages | Use Cases |
|----------|------------|-----------|
| Relationship-Focused Navigation | Sustainable, trust-based, long-term value | Stable organizations, relationship-driven cultures, senior positions |
| Performance-Focused Navigation | Merit-based, measurable, objective | Results-driven cultures, technical organizations, rapid growth companies |

---

## Key Takeaways

✅ **Essential Points**:
- Three-layer organizational mapping reveals how decisions really get made
- Strategic visibility requires intentional positioning and relationship building
- Ethical political navigation focuses on mutual benefit and organizational success
- Career advancement requires both excellent performance and strategic positioning

⚠️ **Common Pitfalls**:
- Assuming that excellent work performance alone will be noticed and rewarded
- Navigating politics through manipulation rather than authentic value creation

## Next Steps
Congratulations! You've completed the Career Advancement course. These organizational navigation skills, combined with your strategic planning, networking, and leadership capabilities, position you for sustained career success. Consider applying these frameworks immediately in your current role.

---
`,
  duration: "45 minutes",
  difficulty: "advanced" as const,
  objectives: [
    "Map organizational power structures",
    "Build cross-level influence",
    "Navigate corporate politics ethically",
    "Position for advancement opportunities"
  ],
  keyTopics: [
    "Organizational Dynamics",
    "Power Mapping",
    "Strategic Positioning",
    "Corporate Politics",
    "Career Advancement"
  ]
};